import { createTheme } from '@material-ui/core/styles';
import Color from 'color';

/** Generate a lighter shade of a color */
const lightenColor = (color, amount) => {
  const rgb = Color(color).rgb().array();
  const newRGB = rgb.map(value => {
    return value + (255 - value) * amount;
  });
  return Color.rgb(newRGB).hex();
};

/** Generate a darker shade of a color */
const darkenColor = (color, amount) => {
  const rgbColor = Color(color).rgb().array();
  const newRGB = rgbColor.map(value => {
    return value - value * amount;
  });
  return Color.rgb(newRGB).hex();
};

const theme = createTheme({
  palette: {
    default: {
      darker: darkenColor(`#323640`, 0.5),
      dark: darkenColor(`#323640`, 0.2),
      main: `#323640`,
      light: lightenColor(`#323640`, 0.2),
      lighter: lightenColor(`#323640`, 0.5),
      contrastText: `#fff`,
    },

    primary: {
      darker: darkenColor(`#ff4830`, 0.5),
      dark: darkenColor(`#ff4830`, 0.2),
      main: `#ff4830`,
      light: lightenColor(`#ff4830`, 0.2),
      lighter: lightenColor(`#ff4830`, 0.5),
      contrastText: `#fff`,
    },
    secondary: {
      darker: darkenColor(`#4068a0`, 0.5),
      dark: darkenColor(`#4068a0`, 0.2),
      main: `#4068a0`,
      light: lightenColor(`#4068a0`, 0.2),
      lighter: lightenColor(`#4068a0`, 0.5),
      contrastText: `#fff`,
    },
    tertiary: {
      darker: darkenColor(`#40a0b0`, 0.5),
      dark: darkenColor(`#40a0b0`, 0.2),
      main: `#40a0b0`,
      light: lightenColor(`#40a0b0`, 0.2),
      lighter: lightenColor(`#40a0b0`, 0.5),
      contrastText: `#fff`,
    },

    utility: {
      darker: darkenColor(`#404860`, 0.5),
      dark: darkenColor(`#404860`, 0.2),
      main: `#404860`,
      light: lightenColor(`#404860`, 0.2),
      lighter: lightenColor(`#404860`, 0.5),
      contrastText: `#fff`,
    },
    error: {
      darker: darkenColor(`#ff2040`, 0.5),
      dark: darkenColor(`#ff2040`, 0.2),
      main: `#ff2040`,
      light: lightenColor(`#ff2040`, 0.2),
      lighter: lightenColor(`#ff2040`, 0.5),
      contrastText: `#fff`,
    },
    warning: {
      darker: darkenColor(`#ffa030`, 0.5),
      dark: darkenColor(`#ffa030`, 0.2),
      main: `#ffa030`,
      light: lightenColor(`#ffa030`, 0.2),
      lighter: lightenColor(`#ffa030`, 0.5),
      contrastText: `#fff`,
    },
    success: {
      darker: darkenColor(`#30b448`, 0.5),
      dark: darkenColor(`#30b448`, 0.2),
      main: `#30b448`,
      light: lightenColor(`#30b448`, 0.2),
      lighter: lightenColor(`#30b448`, 0.5),
      contrastText: `#fff`,
    },
    info: {
      darker: darkenColor(`#2080ff`, 0.5),
      dark: darkenColor(`#2080ff`, 0.2),
      main: `#2080ff`,
      light: lightenColor(`#2080ff`, 0.2),
      lighter: lightenColor(`#2080ff`, 0.5),
      contrastText: `#fff`,
    },

    nav: {
      prod: `#202430`,
      test: `#666d80`,
    },

    ops: {
      main: `#f05020`,
    },
    concierge: {
      main: `#4080c8`,
      loaner: darkenColor(`#4080c8`, 0.333),
    },
    shuttle: {
      main: `#20a090`,
    },
    auto: {
      main: `#20c880`,
    },
    lyft: {
      main: `#ea0b8c`,
    },
    uber: {
      main: `#000000`,
    },
    android: {
      main: `#00de7a`,
    },
    ios: {
      main: `#b3b3b3`,
    },

    text: {
      primary: `#323640`,
      secondary: `#32364096`,
      disabled: `#32364072`,
      hint: `#32364072`,
      contrast: `#ffffff`,
      contrastFade: `#ffffff96`,
      contrastPrimary: `#ffffff`,
      contrastSecondary: `#ffffffa0`,
      contrastDisabled: `#ffffff72`,
    },

    background: {
      paper: `#ffffff`,
      default: `#fafafa`,
      light: `#f8f8f8`,
      main: `#f4f4f4`,
      dark: `#f0f0f0`,
    },

    action: {
      hover: `#00000010`,
      selected: `#00000020`,
      focus: `#00000030`,
      active: `#00000080`,
      disabled: `#00000060`,
      disabledBackground: `#00000030`,
    },

    divider: `#00000016`,
    dividerStrong: `#e0e0e0`,
    drop: `#32364010`,

    fade: [
      `#00000010`,
      `#00000020`,
      `#00000030`,
      `#00000040`,
      `#00000050`,
      `#00000060`,
      `#00000080`,
      `#000000a0`,
      `#000000b4`,
      `#000000c8`,
    ],
  },

  border: [`1px solid #00000020`, `1px solid #00000040`, `1px solid #00000060`, `1px solid #e0e0e0`],

  shape: {
    borderRadius: `4px`,
    paperRadius: `8px`,
  },

  shadow: {
    none: `none`,
    soft: `0 0 16px #00000016`,
    medium: `0 0 16px #00000024`,
    harsh: `0 0 16px #00000040`,
    nav: `0 0 16px #00000040`,
    sharp: `1px 1px 4px #00000040`,
  },

  typography: { useNextVariants: true },
});

export default theme;
